/**
 * Thin lifecycle adapter over Engine. Run end (timer or overflow)
 * submits once via endRun; the shell shows its ended overlay.
 */
import type { GameContext, ScorePayload, ShuvGame } from "@host/types.js";
import { Engine, type RunResult } from "./core/Engine.js";
import { saveRunBundle } from "@host/bundles.js";

export function createTrashPanicZeroGame(ctx: GameContext): ShuvGame {
  const engine = new Engine(ctx);
  let submitted = false;

  const submitRun = (result: RunResult): void => {
    if (submitted) return;
    submitted = true;
    saveRunBundle(result.bundle);
    void ctx
      .endRun?.({
        endReason: result.reason,
        durationMs: result.durationMs,
        portalTraversals: result.portalTraversals,
        bestChain: result.bestCombo,
        metrics: [
          { leaderboardId: "score", value: result.score },
          { leaderboardId: "combo", value: result.bestCombo },
        ],
        eventLedgerHash: result.bundle.final.ledgerHash,
        scoreComponents: {
          loads: result.bundle.loads.reduce((sum, load) => sum + load.score, 0),
          tickets: result.bundle.metrics.ticketCompleted,
        },
        metadata: {
          incinerated: result.incinerated,
          // Precise sim end reason for the shell results copy / diagnostics
          // drawer (LOCKED-DESIGN 18.12): cooked / jam-overload / overflow /
          // complete / abandoned, before the coarse death|cash-out mapping.
          simEndReason: result.simEndReason,
          seedId: result.bundle.seed.id,
          grade: result.bundle.final.grade,
          loads: result.bundle.metrics.loads,
          overflowPeak: result.bundle.metrics.overflowPeak,
          ticketsCompleted: result.bundle.metrics.ticketCompleted,
          runId: result.bundle.runId,
        },
      })
      .catch(() => {
        /* offline / practice runs are fine */
      });
  };

  return {
    async preload(context: GameContext): Promise<void> {
      context.reportProgress?.({ phase: "boot", loaded: 0, total: 1, label: "physics" });
      await engine.init();
      engine.onRunEnd(submitRun);
      // first-time players get the interactive warm-up
      try {
        const saved = await context.load("onboarding");
        engine.setNeedsTutorial(!(saved && saved["done"] === true));
      } catch {
        engine.setNeedsTutorial(true);
      }
      // First-encounter teaching pauses fire once EVER, not once per session
      // (LOCKED-DESIGN 18.10) — seed the flags from the save layer.
      try {
        const seen = await context.load("first-encounters");
        engine.setFirstEncountersSeen({ red: seen?.["red"] === true, jam: seen?.["jam"] === true, stale: seen?.["stale"] === true });
      } catch {
        engine.setFirstEncountersSeen({ red: false, jam: false, stale: false });
      }
      context.reportProgress?.({ phase: "playable", loaded: 1, total: 1, label: "arena" });
      context.reportTelemetry("game_loaded");
    },
    mount(): void {
      engine.mount();
    },
    start(): void {
      engine.start();
      ctx.reportTelemetry("game_started");
    },
    pause(): void {
      engine.pause();
    },
    resume(): void {
      engine.resume();
    },
    restart(): void {
      submitted = false;
      engine.restart();
    },
    destroy(): void {
      engine.destroy();
    },
    getScore(): ScorePayload | null {
      const r = engine.runResult();
      return {
        gameSlug: "trash-panic-zero",
        leaderboardId: "score",
        value: r.score,
        duration: r.durationMs / 1000,
      };
    },
    resize(size): void {
      engine.resize(size);
    },
    setQuality(level): void {
      engine.setQuality(level);
    },
    setHudVisible(visible): void {
      engine.setHudVisible(visible);
    },
    configureRun(seedId, practicePhase): void {
      submitted = false;
      engine.configureRun(seedId, practicePhase);
    },
    startPrologue(): void {
      submitted = false;
      engine.beginPrologue();
    },
    getRunBundle(): unknown {
      return engine.runResult().bundle;
    },
    primeAudio(): void {
      engine.primeAudio();
    },
    isTutorialActive(): boolean {
      return engine.tutorialActive;
    },
    getLiveStatus() {
      return engine.getLiveStatus();
    },
    getFps(): number {
      return engine.fps;
    },
  };
}
